<template>
  <div class="pb-access-request-form">
    <div
      v-if="loading"
      class="pb-access-request-form-loader"
    >
      <Spinner />
    </div>

    <MessageBox
      v-else-if="isMine && translationsLoaded"
      color="info"
      class="pb-half-margin-top"
    >
      {{ translate('pb2.accessRequestForm.isOwnerOf') }} "{{ profile.name }}".
    </MessageBox>

    <template v-else-if="translationsLoaded">
      <MessageBox
        v-if="hasAllPermissions"
        color="info"
        class="pb-half-margin-top"
      >
        {{ translate('pb2.accessRequestForm.hasFullPermissionsOn') }} "{{ profile.name }}".
      </MessageBox>

      <MessageBox
        v-else-if="canRead"
        class="pb-half-margin-vertical"
      >
        {{ translate('pb2.accessRequestForm.currentPermissions') }}
        <ul>
          <li v-if="canRead">
            {{ translate('generic.read') }}
          </li>
          <li v-if="canWrite">
            {{ translate('generic.write') }}
          </li>
          <li v-if="canDelete">
            {{ translate('generic.delete') }}
          </li>
          <li v-if="canCopy">
            {{ translate('generic.copy') }}
          </li>
        </ul>
      </MessageBox>

      <MessageBox
        v-if="existingDemand"
        color="success"
        class="pb-half-margin-top"
      >
        {{ translate('demand.reviewingMsg') }}
        <BaseButton @click="refresh">
          <UiIcon icon="sync" />
        </BaseButton>
      </MessageBox>

      <template v-else-if="!hasAllPermissions">
        <MessageBox
          v-if="!canRead"
          color="info"
          class="pb-half-margin-top"
        >
          {{ translate('demand.requestAccessMsg') }}
        </MessageBox>

        <PbInput
          v-if="!canRead"
          v-tooltip="{ text: translate('demands.ask.read') }"
          class="pb-access-request-form-field"
          :type="INPUT_TYPE_SWITCH"
          :value="true"
          :label="translate('generic.read')"
          :title="translate('demands.ask.read')"
          disabled
        />
        <PbInput
          v-if="!canWrite"
          v-model="writePerm"
          v-tooltip="{ text: translate('demands.ask.readWrite') }"
          class="pb-access-request-form-field"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('generic.write')"
          :title="translate('demands.ask.readWrite')"
        />
        <PbInput
          v-if="!canDelete"
          v-model="deletePerm"
          v-tooltip="{ text: translate('demands.ask.delete') }"
          class="pb-access-request-form-field"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('generic.delete')"
          :title="translate('demands.ask.delete')"
        />
        <PbInput
          v-if="!canCopy"
          v-model="copyPerm"
          v-tooltip="{ text: translate('demands.ask.copy') }"
          class="pb-access-request-form-field"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('generic.copy')"
          :title="translate('demands.ask.copy')"
        />

        <ButtonWithLoader
          v-if="!isMine && !hasAllPermissions"
          :label="translate('demand.requestAccess')"
          class="btn-success"
          :disabled="!canSubmit"
          @click="confirmDemand"
        />
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, watch } from 'vue'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import MessageBox from '@nicolaudie-group/ng-ui-kit/components/MessageBox/MessageBox.vue'
import Spinner from '@nicolaudie-group/ng-ui-kit/components/Spinner/Spinner.vue'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import { INPUT_TYPE_SWITCH } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'
import useStatus from '@nicolaudie-group/ng-ui-kit/composables/useStatus'

import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import useQueryToggle from '@/composables/useQueryToggle'
import useTranslate from '@/composables/useTranslate'
import { useProfile, useProfileStatus } from '@/composables/useProfile'
import * as sdk from '@/helpers/sdk'
import useAction from '@/composables/useAction'
import useProfileStore from '@/stores/profile.store'
import PbInput from '@/components/PbInput.vue'

const props = defineProps({
  write: { type: Boolean, default: false },
  delete: { type: Boolean, default: false },
  copy: { type: Boolean, default: false },
})

const profileStore = useProfileStore()

const { translate, translationsSettled, translationsLoaded } = useTranslate()

const { state: writePerm } = useQueryToggle('requestWrite', { immediate: false })
const { state: deletePerm } = useQueryToggle('requestDelete', { immediate: false })
const { state: copyPerm } = useQueryToggle('requestCopy', { immediate: false })

watch(props, (newProps) => {
  if (newProps.write) {
    writePerm.value = true
  }
  if (newProps.delete) {
    deletePerm.value = true
  }
  if (newProps.copy) {
    copyPerm.value = true
  }
}, { immediate: true })

const profile = useProfile()

const canRead = computed(() => !!profile.value)
const canWrite = computed(() => canRead.value && profile.value.userCanEdit)
const canDelete = computed(() => canRead.value && profile.value.userCanDelete)
const canCopy = computed(() => canRead.value && profile.value.userCanCopy)

watch(deletePerm, (requestDelete) => {
  if (requestDelete) {
    writePerm.value = true
  }
})

const hasAllPermissions = computed(() => (
  canRead.value
  && canWrite.value
  && canDelete.value
  && canCopy.value
))
const isMine = computed(() => canRead.value && profile.value.isMine)

const newPermissions = computed(() => {
  const result = {}
  const newWrite = writePerm.value
  const newDelete = deletePerm.value
  const newCopy = copyPerm.value
  // Only set the properties if they are true
  if (!canRead.value) {
    // Always add read permission to the demand if
    // the user don't have have read access to the profile
    result.read = true
  }
  if (!canWrite.value && newWrite) {
    // Add write permission to the demand if the user doesn't
    // have write access to the profile and requests it
    result.write = true
  }
  if (!canDelete.value && newDelete) {
    // Add delete permission to the demand if the user doesn't
    // have delete access to the profile and requests it
    result.delete = true
  }
  if (!canCopy.value && newCopy) {
    // Add copy permission to the demand if the user doesn't
    // have copy access to the profile and requests it
    result.copy = true
  }
  return result
})

const canSubmit = computed(() => !!Object.keys(newPermissions.value).length)

const { action: getPending, status, data: existingDemand } = useAction(
  async () => {
    const { profileUid } = profileStore
    if (profileUid && !isMine.value) {
      try {
        const result = await sdk.v2.demands.getPending({
          url: {
            category: 'ACCESS',
            targetKind: 'Object#Ssl',
            targetUid: profileUid,
          },
        })
        return result.data
      } catch (result) {
        if (result instanceof sdk.SDKTypes2.Result && result.error.name === 'E_BDD_NOT_FOUND') {
          return null
        }
        throw result
      }
    }
    return null
  },
)

const { pending: loadingDemands } = useStatus(status)
const { settled: profileSettled } = useProfileStatus()

const loading = computed(() => {
  const translationsSettledValue = translationsSettled.value
  const profileSettledValue = profileSettled.value
  const isMineValue = isMine.value
  const demandsLoadingValue = loadingDemands.value
  if (!translationsSettledValue) {
    return true
  }
  if (profileSettledValue) {
    if (isMineValue) {
      return false
    }
    if (!demandsLoadingValue) {
      return false
    }
  }
  return true
})

onBeforeMount(getPending)

const { action: makeDemand } = useAction(
  async () => {
    const { profileUid } = profileStore
    if (profileUid) {
      const result = await sdk.v2.demands.create({
        body: {
          category: 'ACCESS',
          targetKind: 'Object#Ssl',
          targetUid: profileUid,
          data: newPermissions.value,
        },
      })
      return result.data
    }
    return null
  },
  { data: existingDemand }, // Save the created demand in "existingDemand" ref
)

async function refresh() {
  const { profileUid } = profileStore
  await profileStore.get(profileUid)
  await getPending()
}

async function confirmDemand() {
  await makeDemand()
}
</script>

<style lang="scss" src="./AccessRequestForm.scss" />
